import * as React from "react";
import { resolveSuggestionPlainText } from "../../../agreement";
import type { AnalysisParagraphResult } from "../../../agreement";

export function useSuggestionEditor() {
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [editedTextByKey, setEditedTextByKey] = React.useState<Record<string, string>>({});
  const [appliedEditKeys, setAppliedEditKeys] = React.useState<Set<string>>(new Set());
  const [draftText, setDraftText] = React.useState("");

  const isEditing = editingKey !== null;

  const clearEditState = React.useCallback((key: string) => {
    setEditingKey((current) => (current === key ? null : current));
    setEditedTextByKey((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setAppliedEditKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const enterEditMode = React.useCallback(
    (key: string, row: AnalysisParagraphResult) => {
      const plain = editedTextByKey[key] ?? resolveSuggestionPlainText(row);
      setEditingKey(key);
      setDraftText(plain);
    },
    [editedTextByKey]
  );

  const applyEdit = React.useCallback((key: string) => {
    setEditedTextByKey((prev) => ({ ...prev, [key]: draftText }));
    setAppliedEditKeys((prev) => new Set(prev).add(key));
    setEditingKey(null);
  }, [draftText]);

  const cancelEdit = React.useCallback((_key: string) => {
    setEditingKey(null);
    setDraftText("");
  }, []);

  const hasAppliedEdit = React.useCallback(
    (key: string) => appliedEditKeys.has(key) && key in editedTextByKey,
    [appliedEditKeys, editedTextByKey]
  );

  const confirmDiscardEdit = React.useCallback((): boolean => {
    if (!editingKey) return true;
    return window.confirm("Discard edit?");
  }, [editingKey]);

  const getDisplayText = React.useCallback(
    (key: string, _row: AnalysisParagraphResult): string | null => {
      if (hasAppliedEdit(key)) {
        return editedTextByKey[key];
      }
      return null;
    },
    [hasAppliedEdit, editedTextByKey]
  );

  const hasUserEdit = React.useCallback(
    (key: string) => key in editedTextByKey,
    [editedTextByKey]
  );

  return {
    editingKey,
    editedTextByKey,
    draftText,
    isEditing,
    setDraftText,
    clearEditState,
    enterEditMode,
    applyEdit,
    cancelEdit,
    hasAppliedEdit,
    hasUserEdit,
    confirmDiscardEdit,
    getDisplayText,
  };
}
